import { useEffect, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import { Grid2 } from '@mui/material';
import { useAdminCity, useAdminCountry, useAdminState } from '../../assets/api/useAdminApi';
import { AutoCompleteField } from './formfields/AutoCompleteField';
import { TextFormField } from './formfields/TextFormField';
import { useZoneList } from '../../assets/api/useMarketingExecutive';
import { IdAutoCompleteField } from './formfields/IdAutoCompleteField';
import UtItem from '../utils/UtList';
import { useAdminZoneStore } from '../../store/useAdminZoneStore';
import useAuthStore from '../../store/useAuthStore';

export const LocationSelectorField = ({
    countryField,
    stateField,
    cityField,
    zipField,
    Input_Width,
    passedCountryId,
    passedStateId,
    passedCityId,
    isShow,
    isZone = false,
    zoneField,
    isStoredId = false,
    selectedMainRole,
    passedZoneOption,
    isPincode = true

}) => {
    const [searchCity, setSearchCity] = useState("")
    const [searchState, setSearchState] = useState("")
    const selectedZones = useAdminZoneStore((s) => s.selectedZones);
    const addZone = useAdminZoneStore((s) => s.addZone);
    const removeZone = useAdminZoneStore((s) => s.removeZone);
    const { user } = useAuthStore()
    const [pincodeStateName, setPincodeStateName] = useState(null);
    const [pincodeCityName, setPincodeCityName] = useState(null);
    const [countryId, setCountryId] = useState(passedCountryId);
    const [stateId, setStateId] = useState(passedStateId);
    const [cityId, setCityId] = useState(passedCityId);
    const { data: countryList, isLoading: countryLoading } = useAdminCountry();
    const { data: stateList, isLoading: stateLoading } = useAdminState({ country_id: countryId, search: searchState });
    const { data: cityList, isLoading: cityLoading } = useAdminCity({
        state_id: stateId,
        country_id: countryId,
        city_id: passedCityId,
        search: searchCity
    });

    const countryOption = countryList?.data?.map((item) => ({
        id: item?.id,
        label: item?.country_name,
        value: item?.country_name,
        country_id: item?.id
    }));

    const stateOption = stateList?.data?.map((item) => ({
        id: item?.id,
        label: item?.state_name,
        value: item?.state_name,
        state_id: item?.id
    }));

    const cityOption = cityList?.data?.map((item) => ({
        id: item?.id,
        label: item?.city_name,
        value: item?.city_name,
        city_id: item?.id
    }));


    // useEffect(() => {
    //     if (isProfile) {
    //         setCountryId(userDetail?.country_code);
    //         setStateId(userDetail?.state_id);
    //     }

    // }, [userDetail]);


    const { data: ZoneList, isLoading: isLoadingZone } = useZoneList({ city_id: cityId });

    const ZoneOption = ZoneList?.data?.map((item) => ({
        id: item?.id,
        value: item?.zone_name,
        label: item?.zone_name
    })) || [];

    const { setFieldValue, setFieldError, values } = useFormikContext();

    // ✅ STEP 1: Sync stateId after state list loads
    useEffect(() => {
        if (stateList?.data?.length && pincodeStateName) {
            const foundState = stateList.data.find(
                (s) => s.state_name.toLowerCase() === pincodeStateName.toLowerCase()
            );
            if (foundState) {
                setStateId(foundState.id);
                if (isStoredId) {
                    setFieldValue(stateField, foundState.id);
                } else {
                    setFieldValue(stateField, foundState.state_name);
                }
            }
        }
    }, [stateList, pincodeStateName,]);

    // ✅ STEP 2: Sync cityId after city list loads
    useEffect(() => {
        if (cityList?.data?.length && pincodeCityName) {
            const foundCity = cityList.data.find(
                (c) => c.city_name.toLowerCase() === pincodeCityName.toLowerCase()
            );
            if (foundCity) {
                setCityId(foundCity.id);
                if (isStoredId) {
                    setFieldValue(cityField, foundCity.id);
                } else {
                    setFieldValue(cityField, foundCity.city_name);
                }
            }
        }
    }, [cityList, pincodeCityName,]);



    const handlePincodeChange = async (pincode) => {
        setFieldValue(zipField, pincode);

        if (pincode.length === 6) {
            try {
                const res = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
                const data = await res.json();
                if (data[0]?.Status === "Success" && data[0]?.PostOffice?.length) {
                    const postOffice = data[0].PostOffice[0];
                    const countryName = postOffice.Country;
                    const stateName = postOffice.State;
                    const cityName = postOffice.District || postOffice.Name;

                    setPincodeStateName(stateName);
                    setPincodeCityName(cityName);
                    setSearchCity(cityName)
                    setSearchState(stateName)

                    // Set country immediately
                    const selectedCountry = countryOption?.find(
                        (c) => c?.value.toLowerCase() === countryName.toLowerCase()
                    );

                    if (isStoredId) {
                        setFieldValue(countryField, selectedCountry?.id || '');
                    } else {
                        setFieldValue(countryField, countryName || '');
                    }

                    if (selectedCountry) setCountryId(selectedCountry.country_id);

                    setFieldError(zipField, "");
                } else {
                    setPincodeStateName("")
                    setPincodeCityName("")
                    setFieldError(zipField, "Invalid Pincode");
                    setFieldValue(cityField, "");
                    setFieldValue(stateField, "");
                    setFieldValue(countryField, "");
                    setFieldValue(zoneField, "");
                }
            } catch {
                setFieldError(zipField, "Unable to validate pincode");
            }
        } else {
            setPincodeStateName("")
            setPincodeCityName("")
            setFieldError(zipField, "");
            setFieldValue(cityField, "");
            setFieldValue(stateField, "");
            setFieldValue(countryField, "");
            setFieldValue(zoneField, "");
        }
    };

    const handleRemove = (id) => removeZone(id);


    const multiRoles = ["Marketing Executive", "Zonal Coordinator"];
    const isSingleSelect = !selectedMainRole || !multiRoles.includes(selectedMainRole);

    // Sync Formik fields when passed values come from parent
    useEffect(() => {
        // Country
        if (passedCountryId && countryOption?.length) {
            const found = countryOption.find(c => c.id === passedCountryId);

            if (isStoredId) {
                setFieldValue(countryField, passedCountryId);
            } else {
                setFieldValue(countryField, found?.label || "");
            }

            setCountryId(passedCountryId);

        }

        // State
        if (passedStateId && stateOption?.length) {
            const found = stateOption.find(s => s.id === passedStateId);
            setSearchState(found?.value)

            if (isStoredId) {
                setFieldValue(stateField, passedStateId);
            } else {
                setFieldValue(stateField, found?.label || "");
            }

            setStateId(passedStateId);

        }

        // City
        if (passedCityId && cityOption?.length) {
            const found = cityOption.find(c => c.id === passedCityId);
            if (isStoredId) {
                setFieldValue(cityField, passedCityId);

            } else {
                setFieldValue(cityField, found?.label || "");
            }
            setSearchCity(found?.value)
            setCityId(passedCityId);

        }

    }, [
        passedCountryId,
        passedStateId,
        passedCityId,
        countryList,
        stateList,
        cityList
    ]);


    return (
        <>
            {
                isPincode &&
                <Grid2 children size={Input_Width}>
                    <Field
                        name={zipField}
                        component={TextFormField}
                        label="Pincode"
                        isRequired
                        onChange={(e) => handlePincodeChange(e)}

                    />
                </Grid2>
            }

            <Grid2 children size={Input_Width}>
                <Field
                    name={countryField}
                    component={isStoredId ? IdAutoCompleteField : AutoCompleteField}
                    options={countryOption}
                    label="Country"
                    isRequired
                    isLoading={countryLoading}
                    onValueChange={(value) => setCountryId(value?.country_id)}
                    nooptionText={"No Country found"}
                    isShow={isShow}
                    isStoredId={isStoredId}
                    disabled={user?.role === "Marketing Manager"}
                />
            </Grid2>

            <Grid2 children size={Input_Width}>
                <Field
                    name={stateField}
                    component={isStoredId ? IdAutoCompleteField : AutoCompleteField}
                    options={stateOption}
                    label="State"
                    isRequired
                    isLoading={stateLoading}
                    onValueChange={(value) => setStateId(value?.state_id)}
                    nooptionText={"No State found"}
                    isShow={isShow}
                    isStoredId={isStoredId}
                    disabled={user?.role === "Marketing Manager"}
                    onSearchChange={(value) => {
                        setSearchState(value)
                    }}
                />
            </Grid2>

            <Grid2 children size={Input_Width}>
                <Field
                    name={cityField}
                    component={isStoredId ? IdAutoCompleteField : AutoCompleteField}
                    options={cityOption}
                    label="City"
                    isRequired
                    isLoading={cityLoading}
                    onValueChange={(value) => setCityId(value?.city_id)}
                    nooptionText={"No City Found"}
                    isShow={isShow}
                    isStoredId={isStoredId}
                    disabled={user?.role === "Marketing Manager"}
                    onSearchChange={(value) => {
                        setSearchCity(value)
                    }}

                />
            </Grid2>

            {
                !isSingleSelect &&
                <Grid2 children size={12}>


                    {selectedZones?.map((item, index) => (
                        <UtItem key={index} text={item?.label} onRemove={() => { handleRemove(item?.id) }} />
                    ))}
                </Grid2>
            }
            {isZone && (
                <Grid2 children size={Input_Width}>
                    <Field
                        name={zoneField}
                        component={IdAutoCompleteField}
                        options={passedZoneOption || ZoneOption}
                        label="Select Zone"
                        isRequired
                        isLoading={isLoadingZone}
                        isStoredId={true}
                        // onValueChange={(newValue) => {
                        //     setSelectedZones((prev) => {
                        //         const exists = prev?.find((item) => item?.id === newValue?.id);
                        //         if (exists) return prev;
                        //         if (isSingleSelect) {
                        //             return [newValue];
                        //         }

                        //         setFieldValue("cluster_ids", "");
                        //         return [...prev, newValue];

                        //     });

                        // }}
                        onValueChange={(newValue) => {
                            addZone(newValue, isSingleSelect);

                            if (!isSingleSelect) {
                                setFieldValue("cluster_ids", "");
                            }
                        }}



                    />
                </Grid2>
            )}
        </>
    );
};











